/**
 * /track/:token — public client-facing order tracker (private tokenized link).
 * Shows the slow-cure pipeline as a vertical timeline with the current stage lit.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, Loader2, Package, ArrowRight } from 'lucide-react';
import api from '../services/api';

const OrderTrack = () => {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/orders/track/${token}`)
      .then((r) => setOrder(r.data.order))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div
      className="min-h-screen text-stone-900"
      style={{
        background:
          'radial-gradient(circle at 25% 10%, #f7dfa9 0%, transparent 45%), radial-gradient(circle at 80% 80%, #c87f4a 0%, transparent 55%), linear-gradient(135deg, #f3e6cd 0%, #e0b780 55%, #a86838 100%)',
      }}
      data-testid="order-track-page"
    >
      <div className="max-w-2xl mx-auto px-6 py-14">
        <Link to="/" className="text-[10px] tracking-[0.35em] uppercase font-black hover:opacity-60">PRSVR</Link>

        {loading ? (
          <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : error || !order ? (
          <div className="py-28 text-center" data-testid="order-track-notfound">
            <h1 className="text-3xl font-black uppercase mb-3">Order not found</h1>
            <p className="text-sm opacity-70">This tracking link is invalid or has expired. Check the link in your confirmation email.</p>
          </div>
        ) : (
          <>
            <div className="mt-8 mb-2 text-[10px] tracking-[0.4em] uppercase font-black opacity-70">
              {order.category_label}
            </div>
            <h1 className="text-4xl sm:text-5xl font-black uppercase leading-[0.9] tracking-tight mb-2" data-testid="order-track-heading">
              {order.is_complete ? 'On Its Way.' : order.current_stage + '.'}
            </h1>
            <div className="text-sm font-bold tracking-[0.2em] uppercase opacity-60 mb-8" data-testid="order-track-number">
              Order {order.order_number}
            </div>

            {/* Items */}
            {order.items?.length > 0 && (
              <div className="mb-10 border-2 border-stone-900/30 bg-white/40 backdrop-blur p-5">
                <div className="text-[10px] tracking-[0.3em] uppercase font-black mb-3 flex items-center gap-2">
                  <Package className="w-3.5 h-3.5" /> Your Pieces
                </div>
                {order.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm py-1.5 border-b border-stone-900/10 last:border-0">
                    <span className="font-bold">{it.title}{it.variant ? <span className="opacity-60"> · {it.variant}</span> : null}</span>
                    <span className="opacity-70">×{it.quantity}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Timeline */}
            <div className="text-[10px] tracking-[0.3em] uppercase font-black mb-5">The Cure Process</div>
            <ol className="relative" data-testid="order-track-timeline">
              {order.stages.map((stage, i) => {
                const done = i < order.current_index;
                const current = i === order.current_index;
                const last = i === order.stages.length - 1;
                return (
                  <li key={stage} className="flex gap-4 pb-8 relative" data-testid={`order-stage-${i}`}>
                    {!last && (
                      <span
                        className={`absolute left-[15px] top-8 bottom-0 w-0.5 ${done ? 'bg-stone-900' : 'bg-stone-900/20'}`}
                      />
                    )}
                    <span
                      className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 border-2 ${
                        done
                          ? 'bg-stone-900 border-stone-900 text-amber-50'
                          : current
                          ? 'bg-amber-50 border-stone-900 animate-pulse'
                          : 'bg-transparent border-stone-900/30'
                      }`}
                    >
                      {done ? <Check className="w-4 h-4" /> : <span className={`text-[11px] font-black ${current ? '' : 'opacity-40'}`}>{i + 1}</span>}
                    </span>
                    <div className={`pt-1 ${current ? '' : done ? 'opacity-90' : 'opacity-40'}`}>
                      <div className="text-base font-black uppercase tracking-tight leading-none">{stage}</div>
                      {current && (
                        <div className="text-[11px] tracking-[0.15em] uppercase font-bold text-amber-900 mt-1">In progress now</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>

            <div className="mt-6 pt-8 border-t border-stone-900/30">
              <Link to="/dila" className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-black hover:opacity-60">
                Explore the Studio <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderTrack;
